import React, { useEffect, useState } from "react";
import { Container, Table, Form, Row, Col, Button } from "react-bootstrap";
import AccordionComponent from "../../../../common/Accordion";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/Store";
import {
  GetSpecialInstructionData,
  updateMedicareLocation,
} from "../../../../services/SystemLocationAPI";
import Snackbar from "../../../../common/Snackbar";

const AddUpdateInstruction: React.FC<any> = ({
  instructionData,
  instructionOption,
  locationID,
  medicareData,
  setSelectedTab,
  onCancel,
  getMedicareLocationData,
}) => {
  const userID = useSelector((state: RootState) => state.auth.userid);

  const [instructionDetailsKey, setInstructionDetailsKey] = useState<
    string | null
  >("0");

  // Maintain local state for requirement edits
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState(() => {
    if (instructionData && instructionData.length > 0) {
      return instructionData;
    } else {
      // fallback: build structure from instructionOption
      return instructionOption.map((opt: { value: any; label: any }) => ({
        splInstructionId: opt.value,
        splInstructionName: opt.label,
        splInstructionData: "", // empty text area value
        modifiedBy: 0,
        modifiedByName: "-",
        modifiedDate: null,
      }));
    }
  });

  const [instructionDataResult, setInstructionDataResult] = useState<any>(null);

  const getSpecialiInstructionData = async () => {
    if (locationID) {
      const instructionData = await GetSpecialInstructionData(locationID);
      if (instructionData) {
        setInstructionDataResult(instructionData);
      }
    }
  };

  useEffect(() => {
    if (locationID) {
      getSpecialiInstructionData();
    }
  }, [locationID]);

  const handleSave = async () => {
    try {
      let splInstructionsPayload = [];

      if (instructionData && instructionData.length > 0) {
        // ✅ Case 1: Instruction data exists — only send changed ones
        splInstructionsPayload = tableData
          .filter((row: any) => {
            const original = instructionDataResult.find(
              (item: any) => item.splInstructionId === row.splInstructionId
            );
            return original
              ? original.splInstructionData !== (row.splInstructionData || "")
              : true;
          })
          .map((row: any) => ({
            SplInstructionID: row.splInstructionId,
            SplInstructionData: row.splInstructionData || "",
          }));
      } else {
        // ✅ Case 2: No instructionData — send ALL from tableData
        splInstructionsPayload = tableData.map((row: any) => ({
          SplInstructionID: row.splInstructionId,
          SplInstructionData: row.splInstructionData || "",
        }));
      }

      // If nothing changed or nothing to send
      if (splInstructionsPayload.length === 0) {
        setSnackbarMessage("Instruction updated successfully.");
        setError("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onCancel();
        }, 1000);
        return;
      }

      const locationPayload = {
        medicareId: medicareData.medicareId,
        providerName: medicareData.providerName,
        providerAddress: medicareData.providerAddress,
        physicalCity: medicareData.physicalCity,
        physicalState: medicareData.physicalState,
        physicalZip: medicareData.physicalZip,
        telephoneNumber: null,
        faxNumber: null,
        emailId: null,
        countryName: medicareData.countryName,
        providerType: "Medicare",
        latitude: medicareData.latitude,
        longitude: medicareData.longitude,
        county: "New County",
        splInstructions: splInstructionsPayload,
        createdBy: userID,
      };

      const response = await updateMedicareLocation(locationPayload);

      if (response.isSuccess) {
        setSnackbarMessage("Instruction updated successfully.");
        setError("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onCancel();
        }, 1000);
      }
    } catch (error) {
      console.error("Error while saving system location:", error);
      setSnackbarMessage("An unexpected error occurred.");
      setError("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedTab("Details");
    getMedicareLocationData();
  };

  const handleRequirementChange = (index: number, value: string) => {
    const updatedData = [...tableData];
    updatedData[index].splInstructionData = value;
    setTableData(updatedData);
  };

  const instructiondetailAccordion = (
    items: { title: string; content: JSX.Element }[]
  ) => (
    <AccordionComponent
      items={items.map((item) => ({ ...item }))}
      activeKey={instructionDetailsKey}
      onSelect={(key) => setInstructionDetailsKey(key as string)}
    />
  );

  return (
    <div>
      <Container fluid className="px-5">
        <Snackbar
          open={snackbarOpen}
          message={snackbarMessage}
          onClose={() => setSnackbarOpen(false)}
          severity={error}
        />
        {instructiondetailAccordion([
          {
            title: "Location Instructions",
            content: (
              <>
                <Table responsive>
                  <thead>
                    <tr>
                      <th style={{ width: "7%", textAlign: "center" }}>
                        Sr No.
                      </th>
                      <th style={{ width: "25%" }}>Category</th>
                      <th style={{ width: "50%" }}>Description</th>
                      <th style={{ width: "20%" }}>Last Modified By / On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row: any, index: number) => (
                      <tr key={row.splInstructionId}>
                        <td style={{ textAlign: "center" }}>{index + 1}</td>
                        <td>{row.splInstructionName}</td>
                        <td>
                          <Form.Control
                            as="textarea"
                            style={{
                              fontSize: "14px",
                              resize: "none",
                              padding: "4px 10px",
                            }}
                            rows={2}
                            value={row.splInstructionData || ""}
                            onChange={(e) =>
                              handleRequirementChange(index, e.target.value)
                            }
                          />
                        </td>
                        <td style={{ textAlign: "left" }}>
                          {row.modifiedBy !== 0 ? (
                            <>
                              {row.modifiedByName}
                              <br />
                              {new Date(row.modifiedDate).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ),
          },
        ])}
        <Row className="mb-5">
          <Col sm={6} className="d-flex justify-content-start">
            <button
              className="btn d-flex align-items-center btn-text-border me-2"
              onClick={handleCancel}
            >
              <span className="material-icons">arrow_back</span>Back
            </button>
          </Col>
          <Col className="d-flex justify-content-end align-items-end">
            <Button
              variant="outline-info"
              className="d-flex align-items-center"
              onClick={handleCancel}
            >
              <span className="material-icons mr-2">close</span>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="ms-3 d-flex align-items-center"
              onClick={handleSave}
            >
              <span className="material-icons mx-1">save</span>
              Save
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddUpdateInstruction;
